/**
 * CodeMapNode — transform any variable with JavaScript (arrays, objects, filters, etc.)
 */
import React, { useCallback, useState } from "react";
import { Handle, Position } from "reactflow";
import { Braces, Check, Code2, Pencil, Plus, Trash2 } from "lucide-react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const MAX_OUTPUT_VARIABLES = 20;

const newOutputId = () => `out_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const DEFAULT_CODE_MAP_SCRIPT = `// input = selected input variable (JSON auto-parsed)
// get()  = read any flow variable by name
// vars   = all flow variables
`;

const SNIPPETS = [
  {
    label: "Multiple outputs (object)",
    code: `const list = Array.isArray(input) ? input : input?.data?.data ?? [];
return {
  mapped_items: list.map((item) => ({ id: item.id, title: item.name })),
  mapped_items_count: list.length,
};`,
  },
  {
    label: "Filter then map",
    code: `const list = input?.data?.data ?? [];
const filtered = list.filter((item) => item.status === "active");
return {
  active_items: filtered.map((item) => ({ id: item.id, label: item.name })),
  active_count: filtered.length,
};`,
  },
  {
    label: "Single array output",
    code: `const list = input?.data?.data ?? [];
// When returning a non-object, only the first output variable is used
return list.map((item) => ({ id: item.id, title: item.name }));`,
  },
  {
    label: "Carousel-shaped output",
    code: `const list = input?.data?.data ?? [];
return {
  property_cards: list.map((item) => ({
    card_body: \`\${item.name} · \${item.locality}\\n\${item.display_price}\`,
    image_url: item.attachments?.[0]?.url ?? "",
    cta_label: "View",
  })),
  property_cards_count: list.length,
};`,
  },
];

const makeBlankOutput = () => ({ id: newOutputId(), variableName: "" });

const normalizeOutputVariables = (data) => {
  if (Array.isArray(data.outputVariables) && data.outputVariables.length > 0) {
    return data.outputVariables.map((row) => ({
      id: row.id || newOutputId(),
      variableName: row.variableName || "",
    }));
  }
  if (data.outputVariable || data.countVariable) {
    const rows = [];
    if (data.outputVariable) {
      rows.push({ id: newOutputId(), variableName: data.outputVariable });
    }
    if (data.countVariable) {
      rows.push({ id: newOutputId(), variableName: data.countVariable });
    }
    return rows.length > 0 ? rows : [makeBlankOutput()];
  }
  return [makeBlankOutput()];
};

const buildFormState = (data) => ({
  inputVariable: data.inputVariable || "",
  outputVariables: normalizeOutputVariables(data),
  code: data.code != null && data.code !== "" ? data.code : DEFAULT_CODE_MAP_SCRIPT,
});

const CollapsedView = ({ data, onEdit }) => {
  const outputs = normalizeOutputVariables(data).filter((row) => row.variableName);
  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#EFF6FF", padding: "10px", minWidth: "260px", maxWidth: "320px" }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md"
        title="Edit"
      >
        <Pencil size={12} className="text-blue-600" />
      </button>
      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-blue-100 rounded-lg">
            <Code2 size={14} className="text-blue-600" />
          </div>
          <span className="text-[12px] font-bold text-blue-900">Code Transform</span>
        </div>
        {data.inputVariable && (
          <p className="text-[10px] text-gray-600 font-mono">
            in: {`{{${data.inputVariable}}}`}
          </p>
        )}
        {outputs.length > 0 && (
          <p className="text-[10px] text-blue-800 font-mono mt-0.5">
            → {outputs.map((row) => `{{${row.variableName}}}`).join(", ")}
          </p>
        )}
        <p className="text-[9px] text-gray-500 mt-2">JavaScript transform</p>
      </div>
    </div>
  );
};

const OutputVariableRow = ({ row, index, flowVariables, onCreateVariable, onChange, onRemove, canRemove }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] text-gray-400 w-5 shrink-0">{index + 1}.</span>
    <VariableDropdown
      value={row.variableName}
      onChange={(name) => onChange(name || "")}
      flowVariables={flowVariables || []}
      allowCreate
      onCreateVariable={onCreateVariable}
      placeholder="Select output variable…"
      className="flex-1"
    />
    {canRemove && (
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
        title="Remove output"
      >
        <Trash2 size={14} />
      </button>
    )}
  </div>
);

const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => buildFormState(data));
  const [saveError, setSaveError] = useState("");

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateOutput = (idx, variableName) => {
    setForm((prev) => ({
      ...prev,
      outputVariables: prev.outputVariables.map((row, i) =>
        i === idx ? { ...row, variableName } : row
      ),
    }));
  };

  const addOutput = () => {
    setForm((prev) => {
      if (prev.outputVariables.length >= MAX_OUTPUT_VARIABLES) return prev;
      return { ...prev, outputVariables: [...prev.outputVariables, makeBlankOutput()] };
    });
  };

  const removeOutput = (idx) => {
    setForm((prev) => ({
      ...prev,
      outputVariables: prev.outputVariables.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = () => {
    const cleanedOutputs = form.outputVariables
      .map((row) => ({
        id: row.id || newOutputId(),
        variableName: (row.variableName || "").trim(),
      }))
      .filter((row) => row.variableName);

    if (cleanedOutputs.length === 0) {
      setSaveError("Add at least one output variable");
      return;
    }
    if (!form.code.trim()) {
      setSaveError("Write JavaScript that returns your transformed data");
      return;
    }
    setSaveError("");
    onSave({
      label: "Code Transform",
      inputVariable: form.inputVariable.trim(),
      outputVariables: cleanedOutputs,
      outputVariable: cleanedOutputs[0].variableName,
      code: form.code,
    });
  };

  return (
    <div className="nowheel space-y-4 max-w-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
        <p className="text-[12px] font-semibold text-slate-800 mb-1">General-purpose transform</p>
        <p className="text-[11px] text-slate-600 leading-relaxed">
          Write JavaScript to reshape <strong>any</strong> API response. Return an{" "}
          <code className="text-[10px] bg-white px-1 rounded">object</code> to set multiple output
          variables at once, or return a single value (array/string/number) for the first output only.
        </p>
      </div>

      {saveError && (
        <p className="text-[11px] font-semibold text-red-600 bg-red-50 px-3 py-2 rounded-lg">{saveError}</p>
      )}

      <NodeEditorTabs nodeType="codeMap">
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase">Input variable</label>
          <p className="text-[9px] text-gray-400 mb-1">Exposed as <code className="bg-gray-100 px-1 rounded">input</code> in your code</p>
          <VariableDropdown
            value={form.inputVariable}
            onChange={(name) => updateField("inputVariable", name)}
            flowVariables={flowVariables || []}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Select input variable (optional)"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div>
              <label className="text-[10px] font-bold text-gray-600 uppercase">Output variables</label>
              <p className="text-[9px] text-gray-400 mt-0.5">
                Return <code className="bg-gray-100 px-1 rounded">{`{ key: value }`}</code> — keys must match these names
              </p>
            </div>
            <button
              type="button"
              onClick={addOutput}
              disabled={form.outputVariables.length >= MAX_OUTPUT_VARIABLES}
              className="text-[10px] font-semibold text-blue-700 flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 disabled:opacity-40"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="space-y-2">
            {form.outputVariables.map((row, idx) => (
              <OutputVariableRow
                key={row.id}
                row={row}
                index={idx}
                flowVariables={flowVariables}
                onCreateVariable={onCreateVariable}
                onChange={(name) => updateOutput(idx, name)}
                onRemove={() => removeOutput(idx)}
                canRemove={form.outputVariables.length > 1}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label className="text-[10px] font-bold text-gray-600 uppercase flex items-center gap-1">
              <Code2 size={12} /> JavaScript
            </label>
            <div className="flex flex-wrap gap-1">
              {SNIPPETS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => updateField("code", s.code)}
                  className="text-[9px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={form.code}
            onChange={(e) => updateField("code", e.target.value)}
            spellCheck={false}
            rows={16}
            className="w-full px-3 py-2 text-[12px] font-mono leading-relaxed bg-[#1e1e1e] text-[#d4d4d4] rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-y min-h-[240px]"
          />
          <p className="text-[9px] text-gray-400 mt-1.5">
            Available: <code className="bg-gray-100 px-1 rounded">input</code>,{" "}
            <code className="bg-gray-100 px-1 rounded">get(&apos;var&apos;)</code>,{" "}
            <code className="bg-gray-100 px-1 rounded">vars</code>, standard JS (JSON, Math, Array, …). No network or file access.
          </p>
        </div>
      </NodeEditorTabs>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          className="flex-1 py-2.5 text-[12px] font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-1"
        >
          <Check size={14} /> Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 text-[12px] font-bold bg-white text-gray-600 rounded-lg border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const CodeMapNode = ({ id, data }) => {
  const nodeType = data.__nodeType || "codeMap";
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta(nodeType === "jsonMap" ? "codeMap" : nodeType);

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]
  );

  return (
    <div className="relative transition-all shadow-md" style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-blue-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || "Code Transform"}
        subtitle="JavaScript variable mapping"
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
        />
      </FlowNodeEditorShell>
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-blue-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default CodeMapNode;
